import React, { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { withNavigate } from "../authentication/pages/navigateWrapper";
import { containerVariants, itemVariants, PRODUCTS_CONFIG } from "./constants";
import { useProductStatus } from "./hooks/useProductStatus";
import { ProductCard } from "./components/ProductCard";
import MainLayout from "../../shared/components/MainLayout";
import Loader from "../../shared/components/loader/Loader";
import { ProductConfig } from "./components/types";
import Button2 from "../../shared/components/button/Button2";
import { useGlobalContext } from "../../context/GlobalContext";

const ProductPage = () => {
  const navigate = useNavigate();
  const { productStatuses, isLoading, error } = useProductStatus();
  const { isMarketplace } = useGlobalContext();

  const handleProductAction = useCallback(
    (productCode: string) => {
      const config = Object.values(PRODUCTS_CONFIG).find(
        (p) => p.appCode === productCode
      );
      const status = productStatuses[config?.appCode!];
      if (!config) return;
      if (!status) return;

      const role = window.localStorage.getItem("role");
      if (role === "0") {
        const token = window.localStorage.getItem("token");
        window.location.href = config.baseUrl + `?token=${token}`;
      } else if (status.isPurchased || status.isExipred) {
        navigate(`/detailProduct/${productCode}`, {
          state: {
            product: config,
            paidProductInfo: status.paidDetails,
          },
        });
      } else if (!status.isPurchased && status.isEnabled) {
        window.location.href = config.baseUrl;
      } else {
        navigate(`/private/productListV3/${config.appCode}`);
      }
    },
    [navigate, productStatuses]
  );

  const cardVariants = {
    initial: { scale: 1 },
    hover: {
      scale: 1.03,
      transition: {
        duration: 0.3,
        ease: "easeOut",
      },
    },
  };

  const handlePlanUpdateAction = useCallback(
    (productCode: string) => {
      if (isMarketplace) {
        window.open("https://console.cloud.google.com/marketplace", "_blank");
      }
      else if (productStatuses[productCode]?.isUpdateAvailable && !isMarketplace) {
        navigate(`/private/productListV3/${productCode}`, {
          state: {
            tierBought: productStatuses[productCode]?.paidDetails?.tier,
            product: productStatuses[productCode]?.paidDetails?.name,
          },
        });
      }
    },
    [navigate, productStatuses, isMarketplace]
  );

  const renderProductCard = useMemo(() => {
    return Object.values({ NEXBOT: PRODUCTS_CONFIG.NEXBOT }).map((product) => (
      <ProductCard
        key={product.id}
        config={product as any}
        status={
          productStatuses[product.appCode] || {
            isPurchased: false,
            isEnabled: false,
            paidDetails: null,
            isExipred: false,
          }
        }
        onAction={() => handleProductAction(product.appCode)}
        isUpdatePlan={productStatuses[product.appCode]?.isUpdateAvailable}
        planUpdateAction={() => handlePlanUpdateAction(product.appCode)}
      />
    ));
  }, [handlePlanUpdateAction, handleProductAction, productStatuses]);

  return (
    <MainLayout>
      <Loader isLoading={isLoading} />
      <main className="px-4 py-12 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Our Products
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Choose the perfect solution for your needs
          </p>
        </motion.div>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="show"
          transition={{ duration: 0.3 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-7xl mx-auto"
        >
          {renderProductCard}
        </motion.div>
      </main>
    </MainLayout>
  );
};

export default withNavigate(ProductPage);
